/**
 * /pipeline-facility/tickets/:id — one PF field ticket (fieldiq-api#1227, PF-4).
 *
 * WHAT MAY BE EDITED (pfTicketEditMode, mirroring the API):
 *   draft              lines and header edit freely.
 *   office_approved,   an edit returns the ticket to draft. The screen does
 *   customer_rejected  not offer the edit until the office says "unlock" to a
 *                      sentence saying so; when a write then comes back
 *                      `reverted_to_draft: true` the screen says that too.
 *   customer_approved  frozen. Only the internal notes change.
 *
 * Refresh from logs redraws the 'auto' lines and says FIRST that an office
 * price on one of them is lost (manual lines are kept). The PDF is offered once
 * the office has signed (a draft has no snapshot). No modals: every confirm is
 * inline and every refusal is a sentence in the refusal style.
 *
 * PREFLIGHT (PF-6, fieldiq-api#1236) — for the AI review roles (admin,
 * office_staff, supervisor) on any ticket the customer has not approved. It
 * shows the API's flags (unpriced lines, days on no ticket, crew / units /
 * rent-ins with no rate) in place, then the AI's explanation as a draft that
 * waits for approval like every other AI draft.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FileText, Lock, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, getBlobApiErrorMessage } from '../../../api/client';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { usePfMeta } from '../../../hooks/usePipelineFacility';
import { useDeletePfTicket, usePfRates, usePfTicket, useRefreshPfTicket, useUpdatePfTicket, type PfTicketEditResult } from '../../../hooks/usePfTickets';
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, showBlobInTab } from '../../../lib/blob-tab';
import { formatInstant } from '../../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { canWritePfTickets, pfScopeText, pfTicketEditMode, pfTicketHasSnapshot, pfTicketRefusal, pfTicketStatusSkin, PF_REFRESH_WARNING } from '../../../lib/pf-tickets';
import { blankToNull } from '../../../lib/pipeline-facility';
import type { PfTicket, PfTicketHeaderWrite } from '../../../types/pf-tickets';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList } from '../../downhole/recordPage';
import { isNotFound, PF_EYEBROW, PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfBadge, PfEmpty, PfField, PfFormError, PfLoading, PfPanel, PfSpec } from '../PfBits';
import { PfInlineConfirm } from '../digs/DigBits';
import { PfRevertedNotice } from './TicketBits';
import { PfTicketLines } from './TicketLines';
import { PfTicketSignOff } from './TicketSignOff';
import { PfTicketInvoiceLink } from '../invoices/InvoiceBits';
import { useRunPfAi } from '../../../hooks/usePfAi';
import { canUsePfAi, pfAiRefusal } from '../../../lib/pf-ai';
import type { PfAiRunResult } from '../../../types/pf-ai';
import { PfAiRunInPlace, PfPreflightButton, pfTicketPreflightable } from '../ai/PfAiBits';

const HEADER_FIELDS: { key: keyof PfTicketHeaderWrite; read: keyof PfTicket; label: string; max: number }[] = [
  { key: 'lsd', read: 'lsd', label: 'LSD / legal location', max: 100 },
  { key: 'afe_number', read: 'afeNumber', label: 'AFE number', max: 100 },
  { key: 'po_number', read: 'poNumber', label: 'PO number', max: 255 },
  { key: 'cost_code', read: 'costCode', label: 'Cost code', max: 100 },
];

function HeaderForm({ ticket, pending, error, onCancel, onSubmit }: {
  ticket: PfTicket;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (body: PfTicketHeaderWrite) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = { customer_notes: ticket.customerNotes ?? '' };
    HEADER_FIELDS.forEach((f) => { v[f.key] = (ticket[f.read] as string | null) ?? ''; });
    return v;
  });
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setValues((v) => ({ ...v, [k]: e.target.value }));
  return (
    <div data-testid="pf-ticket-header-form" className="space-y-3 border-t border-fieldiq-border px-4 py-4">
      <PfFormError message={error} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {HEADER_FIELDS.map((f) => (
          <PfField key={f.key} label={f.label}>
            <input className={PF_INPUT} maxLength={f.max} value={values[f.key]} onChange={set(f.key)} />
          </PfField>
        ))}
      </div>
      <PfField label="Notes for the customer" hint="Printed on the ticket.">
        <textarea className={`${PF_INPUT} h-24 py-2`} maxLength={5000} value={values.customer_notes} onChange={set('customer_notes')} />
      </PfField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={PF_SECONDARY} onClick={onCancel} disabled={pending}>Cancel</button>
        <button
          type="button"
          className={PF_PRIMARY}
          disabled={pending}
          onClick={() => {
            const body: PfTicketHeaderWrite = {};
            const cur: Record<string, string | null> = { customer_notes: ticket.customerNotes };
            HEADER_FIELDS.forEach((f) => { cur[f.key] = ticket[f.read] as string | null; });
            Object.keys(values).forEach((k) => {
              const next = blankToNull(values[k]);
              if (next !== (cur[k] ?? null)) (body as Record<string, string | null>)[k] = next;
            });
            onSubmit(body);
          }}
        >
          {pending ? 'Saving…' : 'Save details'}
        </button>
      </div>
    </div>
  );
}

function InternalNotes({ ticket, canWrite }: { ticket: PfTicket; canWrite: boolean }) {
  const update = useUpdatePfTicket();
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(ticket.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  return (
    <PfPanel
      eyebrow="Internal"
      title="Office notes"
      testId="pf-ticket-notes"
      actions={canWrite && !editing ? <button type="button" className={PF_SECONDARY} onClick={() => { setNotes(ticket.notes ?? ''); setError(null); setEditing(true); }}>Edit notes</button> : undefined}
    >
      {editing ? (
        <div className="space-y-3 px-4 py-4">
          <PfFormError message={error} />
          <PfField label="Notes" hint="Never printed on the ticket, so they can change at any status.">
            <textarea className={`${PF_INPUT} h-24 py-2`} maxLength={5000} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </PfField>
          <div className="flex justify-end gap-2">
            <button type="button" className={PF_SECONDARY} onClick={() => setEditing(false)} disabled={update.isPending}>Cancel</button>
            <button
              type="button"
              className={PF_PRIMARY}
              disabled={update.isPending}
              onClick={() =>
                update.mutate(
                  { id: ticket.id, body: { notes: blankToNull(notes) } },
                  { onSuccess: () => { toast.success('Notes saved'); setEditing(false); }, onError: (e) => setError(pfTicketRefusal(e, 'The notes could not be saved.')) },
                )
              }
            >
              {update.isPending ? 'Saving…' : 'Save notes'}
            </button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap px-4 py-4 text-sm text-fieldiq-text">{ticket.notes || '—'}</p>
      )}
    </PfPanel>
  );
}

export default function PfTicketDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/pipeline-facility/tickets');
  const { user } = useAuth();
  const canWrite = canWritePfTickets(user?.role);
  const query = usePfTicket(id);
  const catalog = usePfMeta().data?.data ?? null;
  const ratesQuery = usePfRates(undefined, canWrite);
  const update = useUpdatePfTicket();
  const refresh = useRefreshPfTicket();
  const del = useDeletePfTicket();
  const [reverted, setReverted] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [unpricedIds, setUnpricedIds] = useState<string[]>([]);
  const preflight = useRunPfAi();
  const [preflightResult, setPreflightResult] = useState<PfAiRunResult | null>(null);
  const [preflightError, setPreflightError] = useState<string | null>(null);
  const canPreflight = canUsePfAi(user?.role);
  const t = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const tz = meta?.timeZone ?? null;
  const failure = queryLoadFailure(query);
  const mode = pfTicketEditMode(t?.status);
  const editable = canWrite && (mode === 'free' || (mode === 'reverts' && unlocked));

  const onEdited = (r: PfTicketEditResult) => {
    if (r.reverted) {
      setReverted(true);
      setUnlocked(false);
    }
  };

  const openPdf = async () => {
    if (!t) return;
    setPdfError(null);
    const win = claimBlobTab();
    if (!win) return setPdfError(POPUP_BLOCKED_MESSAGE);
    setPdfBusy(true);
    try {
      const res = await api.pipelineFacility.tickets.pdf(t.id);
      showBlobInTab(win, res.data);
    } catch (e) {
      win.close();
      setPdfError(await getBlobApiErrorMessage(e, 'The PDF could not be made.'));
    } finally {
      setPdfBusy(false);
    }
  };

  const runPreflight = () => {
    if (!t) return;
    setPreflightError(null);
    preflight.mutate(
      { agent: 'pf_ticket_preflight', ticketId: t.id },
      {
        onSuccess: (r) => setPreflightResult(r ?? null),
        onError: (e) => setPreflightError(pfAiRefusal(e, 'The preflight did not run.')),
      },
    );
  };

  return (
    <div data-testid="pf-ticket-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title={t?.ticketNumber || (query.isLoading ? 'Field ticket' : '—')}
        description={t ? `${t.customerName ?? '—'} · ${t.jobNumber ?? '—'} · ${pfScopeText(t)}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          t ? (
            <div className="flex flex-wrap gap-2">
              {pfTicketHasSnapshot(t.status) && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={openPdf} disabled={pdfBusy}>
                  <FileText className="size-4" aria-hidden="true" />
                  {pdfBusy ? 'Opening…' : 'PDF'}
                </button>
              )}
              {canPreflight && pfTicketPreflightable(t.status) && (
                <PfPreflightButton busy={preflight.isPending} onClick={runPreflight} className={DH_BAND_SECONDARY} testId="pf-ticket-preflight" />
              )}
              {canWrite && t.status !== 'customer_approved' && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <PfLoading label="Loading ticket" />
          ) : isNotFound(queryFailureError(query)) || (!failure && !t) ? (
            <PfPanel eyebrow="Field ticket" title="Field ticket">
              <PfEmpty>This ticket was not found. It may have been deleted.</PfEmpty>
            </PfPanel>
          ) : failure || !t ? (
            <PfPanel eyebrow="Field ticket" title="Field ticket">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this ticket" />
            </PfPanel>
          ) : (
            <>
              <PfFormError message={pdfError} />
              <PfFormError message={preflightError} />
              {preflightResult && (preflightResult.data as { ticket?: { id?: string } } | null)?.ticket?.id === t.id && (
                <PfAiRunInPlace result={preflightResult} canReview={canPreflight} />
              )}
              {reverted && <PfRevertedNotice onDismiss={() => setReverted(false)} />}
              {deleting && (
                <PfInlineConfirm
                  testId="pf-ticket-delete-confirm"
                  message={`Delete ${t.ticketNumber}? Its lines go with it. The job's daily logs are not touched, so it can be drafted again.`}
                  confirmLabel="Delete ticket"
                  cancelLabel="Keep it"
                  pending={del.isPending}
                  error={deleteError}
                  onCancel={() => setDeleting(false)}
                  onConfirm={() =>
                    del.mutate(t.id, {
                      onSuccess: () => {
                        toast.success(`${t.ticketNumber} deleted`);
                        navigate('/pipeline-facility/tickets', { replace: true });
                      },
                      onError: (e) => setDeleteError(pfTicketRefusal(e, 'The ticket could not be deleted.')),
                    })
                  }
                />
              )}
              {canWrite && mode === 'reverts' && !unlocked && (
                <div className="flex flex-wrap items-center gap-3">
                  {unlocking ? (
                    <PfInlineConfirm
                      testId="pf-ticket-unlock-confirm"
                      message="Editing this ticket returns it to draft: the office approval is withdrawn, and any review link sent to the customer stops working. It has to be approved and sent again."
                      confirmLabel="Unlock to edit"
                      cancelLabel="Leave it signed"
                      onCancel={() => setUnlocking(false)}
                      onConfirm={() => { setUnlocked(true); setUnlocking(false); }}
                    />
                  ) : (
                    <button type="button" className={PF_SECONDARY} onClick={() => setUnlocking(true)} data-testid="pf-ticket-unlock">
                      <Lock className="size-4" aria-hidden="true" />
                      Edit this ticket
                    </button>
                  )}
                </div>
              )}
              {mode === 'frozen' && (
                <p data-testid="pf-ticket-frozen" className="text-sm text-fieldiq-muted">
                  The customer has approved this ticket, so what it bills cannot change. Office notes can still be edited.
                </p>
              )}
              <PfPanel
                eyebrow="Field ticket"
                title="Details"
                testId="pf-ticket-details"
                actions={editable && !editingHeader ? <button type="button" className={PF_SECONDARY} onClick={() => { setHeaderError(null); setEditingHeader(true); }}>Edit details</button> : undefined}
              >
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <PfSpec label="Status" value={<PfBadge skin={pfTicketStatusSkin(t.status)} value={t.status} />} />
                  <PfSpec label="Job" value={t.jobNumber ? <Link className="underline" to={`/pipeline-facility/jobs/${t.jobId}?tab=tickets`}>{t.jobNumber}</Link> : '—'} mono />
                  <PfSpec label="Customer" value={t.customerName || '—'} />
                  <PfSpec label="Covers" value={pfScopeText(t)} />
                  <PfSpec label="Site" value={t.siteName || '—'} />
                  <PfSpec label="Province / state" value={t.provinceState || '—'} />
                  <PfSpec label="Currency" value={t.currency || '—'} />
                  <PfSpec label="Created" value={t.createdAt ? formatInstant(t.createdAt, tz) ?? '—' : '—'} />
                  {HEADER_FIELDS.map((f) => (
                    <PfSpec key={f.key} label={f.label} value={(t[f.read] as string | null) || '—'} />
                  ))}
                </dl>
                {t.customerNotes && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{t.customerNotes}</p>}
                {editable && editingHeader && (
                  <HeaderForm
                    ticket={t}
                    pending={update.isPending}
                    error={headerError}
                    onCancel={() => setEditingHeader(false)}
                    onSubmit={(body) => {
                      if (!Object.keys(body).length) return setHeaderError('Nothing has changed.');
                      setHeaderError(null);
                      update.mutate({ id: t.id, body }, {
                        onSuccess: (r) => { toast.success('Details saved'); setEditingHeader(false); onEdited(r); },
                        onError: (e) => setHeaderError(pfTicketRefusal(e, 'The details could not be saved.')),
                      });
                    }}
                  />
                )}
              </PfPanel>
              {editable && (
                <div className="space-y-2">
                  {refreshing ? (
                    <PfInlineConfirm
                      testId="pf-ticket-refresh-confirm"
                      message={PF_REFRESH_WARNING}
                      confirmLabel="Refresh from the logs"
                      cancelLabel="Not now"
                      pending={refresh.isPending}
                      error={refreshError}
                      onCancel={() => setRefreshing(false)}
                      onConfirm={() =>
                        refresh.mutate(t.id, {
                          onSuccess: (r) => { toast.success('Lines redrawn from the daily logs'); setRefreshing(false); onEdited(r); },
                          onError: (e) => setRefreshError(pfTicketRefusal(e, 'The ticket could not be refreshed.')),
                        })
                      }
                    />
                  ) : (
                    <button type="button" className={PF_SECONDARY} data-testid="pf-ticket-refresh" onClick={() => { setRefreshError(null); setRefreshing(true); }}>
                      <RefreshCw className="size-4" aria-hidden="true" />
                      Refresh from the daily logs
                    </button>
                  )}
                </div>
              )}
              <PfTicketLines
                ticket={t}
                editable={editable}
                canWrite={canWrite}
                rates={ratesQuery.data?.data ?? []}
                catalog={catalog}
                highlight={unpricedIds}
                onEdited={onEdited}
              />
              {t.status === 'customer_approved' && <PfTicketInvoiceLink ticketId={t.id} customerId={t.customerId} />}
              <PfTicketSignOff ticket={t} canWrite={canWrite} tz={tz} today={meta?.today ?? null} onUnpriced={setUnpricedIds} />
              <InternalNotes ticket={t} canWrite={canWrite} />
            </>
          )}
        </div>
      </section>
    </div>
  );
}
